import {
  createFeatureSelector,
  createSelector,
  DefaultProjectorFn,
  MemoizedSelector,
} from '@ngrx/store';

import {
  MarketAnalysisProduct,
  MarketAnalysisResult,
} from '../../../drugs/models/drug-analysis.model';
import { CompetitorChartData, CompetitorSubMarket } from '../../models/competitor-chart-data.model';
import { Competitor } from '../../models/competitor.model';
import { PriceDevelopmentChartData } from '../../models/price-development-chart-data.model';
import {
  ProductAnalysisSettings,
  SettingValueKind,
} from '../../models/product-analysis-settings.model';
import { PurchaseProduct } from '../../models/purchase-product.model';
import { productOverviewFeature } from './product-overview.reducer';
import {
  AnalysisSettingsState,
  CompetitorsState,
  IpgSummaryState,
  PriceDevelopmentState,
  ProductOverviewState,
  ProductState,
  PurchaseProductsState,
  SalesDevelopmentState,
} from './product-overview.state';

// Product-level market shares from the analysis settings. Percentages are 0-100.
export interface MarketShares {
  mat: number | null;
  // MAT can be a percentage or a number of packs.
  matKind: SettingValueKind;
  launch: number | null;
}

export interface CompetitorsTotals {
  primaryMatPrice: number;
  primaryMatPacks: number;
  hospitalMatPrice: number;
  hospitalMatPacks: number;
}

export const EMPTY_TOTALS: CompetitorsTotals = {
  primaryMatPrice: 0,
  primaryMatPacks: 0,
  hospitalMatPrice: 0,
  hospitalMatPacks: 0,
};

export const selectProductOverviewState: MemoizedSelector<
  object,
  ProductOverviewState,
  DefaultProjectorFn<ProductOverviewState>
> = createFeatureSelector<ProductOverviewState>(productOverviewFeature.name);

export const selectProductId: MemoizedSelector<
  object,
  string | null,
  (s1: ProductOverviewState) => string | null
> = createSelector(selectProductOverviewState, state => state.productId);

export const selectChartPeriodMonths: MemoizedSelector<
  object,
  number,
  (s1: ProductOverviewState) => number
> = createSelector(selectProductOverviewState, state => state.chartPeriodMonths);

export const selectCompetitorsState: MemoizedSelector<
  object,
  CompetitorsState,
  (s1: ProductOverviewState) => CompetitorsState
> = createSelector(selectProductOverviewState, state => state.competitors);

export const selectCompetitors: MemoizedSelector<
  object,
  Competitor[],
  (s1: CompetitorsState) => Competitor[]
> = createSelector(selectCompetitorsState, state => state.competitors);

// Dormant = no sales in the past year and no price in the current rate (decided by the API).
export const selectActiveCompetitors: MemoizedSelector<
  object,
  Competitor[],
  (s1: Competitor[]) => Competitor[]
> = createSelector(selectCompetitors, competitors =>
  competitors.filter(competitor => !competitor.isDormant)
);

export const selectDormants: MemoizedSelector<
  object,
  Competitor[],
  (s1: Competitor[]) => Competitor[]
> = createSelector(selectCompetitors, competitors =>
  competitors.filter(competitor => competitor.isDormant)
);

export const selectCompetitorsTotals: MemoizedSelector<
  object,
  CompetitorsTotals,
  (s1: Competitor[]) => CompetitorsTotals
> = createSelector(
  selectActiveCompetitors,
  (competitors): CompetitorsTotals =>
    competitors.reduce(
      (totals: CompetitorsTotals, competitor: Competitor): CompetitorsTotals => ({
        primaryMatPrice:
          totals.primaryMatPrice + competitor.primaryMarketMovement.matPharmacyPurchasePrice.value,
        primaryMatPacks: totals.primaryMatPacks + competitor.primaryMarketMovement.matPacks.value,
        hospitalMatPrice:
          totals.hospitalMatPrice +
          competitor.hospitalMarketMovement.matPharmacyPurchasePrice.value,
        hospitalMatPacks:
          totals.hospitalMatPacks + competitor.hospitalMarketMovement.matPacks.value,
      }),
      EMPTY_TOTALS
    )
);

export const selectCompetitorsLoading: MemoizedSelector<
  object,
  boolean,
  (s1: CompetitorsState) => boolean
> = createSelector(selectCompetitorsState, state => state.loading);

export const selectPriceDevelopmentState: MemoizedSelector<
  object,
  PriceDevelopmentState,
  (s1: ProductOverviewState) => PriceDevelopmentState
> = createSelector(selectProductOverviewState, state => state.priceDevelopment);

export const selectPriceDevelopmentData: MemoizedSelector<
  object,
  PriceDevelopmentChartData | null,
  (s1: PriceDevelopmentState) => PriceDevelopmentChartData | null
> = createSelector(selectPriceDevelopmentState, state => state.data);

export const selectIpgSummaryState: MemoizedSelector<
  object,
  IpgSummaryState,
  (s1: ProductOverviewState) => IpgSummaryState
> = createSelector(selectProductOverviewState, state => state.ipgSummary);

export const selectIpgSummaryResults: MemoizedSelector<
  object,
  MarketAnalysisResult[] | null,
  (s1: IpgSummaryState) => MarketAnalysisResult[] | null
> = createSelector(selectIpgSummaryState, state => state.data);

// The country of the IPG summary result that contains the overview product.
export const selectProductCountryCode: MemoizedSelector<
  object,
  string | null,
  (s1: MarketAnalysisResult[] | null, s2: string | null) => string | null
> = createSelector(
  selectIpgSummaryResults,
  selectProductId,
  (results, productId) =>
    (results ?? []).find((result: MarketAnalysisResult) =>
      result.products.some((product: MarketAnalysisProduct) => product.id === productId)
    )?.countryCode ?? null
);

export const selectProductState: MemoizedSelector<
  object,
  ProductState,
  (s1: ProductOverviewState) => ProductState
> = createSelector(selectProductOverviewState, state => state.product);

// The product's drug - the sales side of its links. Null until loaded.
export const selectDrugId: MemoizedSelector<
  object,
  string | null,
  (s1: ProductState) => string | null
> = createSelector(selectProductState, state => state.data?.drugId ?? null);

export const selectAnalysisSettingsState: MemoizedSelector<
  object,
  AnalysisSettingsState,
  (s1: ProductOverviewState) => AnalysisSettingsState
> = createSelector(selectProductOverviewState, state => state.analysisSettings);

export const selectAnalysisSettings: MemoizedSelector<
  object,
  ProductAnalysisSettings | null,
  (s1: AnalysisSettingsState) => ProductAnalysisSettings | null
> = createSelector(selectAnalysisSettingsState, state => state.data);

// Null until the settings are loaded.
export const selectMarketShares: MemoizedSelector<
  object,
  MarketShares | null,
  (s1: ProductAnalysisSettings | null) => MarketShares | null
> = createSelector(selectAnalysisSettings, settings =>
  settings
    ? {
        mat: settings.productLevel?.marketShareMat?.value ?? null,
        matKind: settings.productLevel?.marketShareMat?.kind ?? SettingValueKind.Percentage,
        launch: settings.productLevel?.marketShareLaunch?.value ?? null,
      }
    : null
);

export function selectPurchaseProductsState(
  subMarket: CompetitorSubMarket
): MemoizedSelector<
  object,
  PurchaseProductsState,
  (s1: ProductOverviewState) => PurchaseProductsState
> {
  return createSelector(selectProductOverviewState, state => state.purchaseProducts[subMarket]);
}

export function selectPurchaseProducts(
  subMarket: CompetitorSubMarket
): MemoizedSelector<object, PurchaseProduct[], (s1: PurchaseProductsState) => PurchaseProduct[]> {
  return createSelector(selectPurchaseProductsState(subMarket), state => state.data ?? []);
}

export function selectSalesDevelopmentState(
  subMarket: CompetitorSubMarket
): MemoizedSelector<
  object,
  SalesDevelopmentState,
  (s1: ProductOverviewState) => SalesDevelopmentState
> {
  return createSelector(selectProductOverviewState, state => state.salesDevelopment[subMarket]);
}

export function selectSalesDevelopmentData(
  subMarket: CompetitorSubMarket
): MemoizedSelector<
  object,
  CompetitorChartData | null,
  (s1: SalesDevelopmentState) => CompetitorChartData | null
> {
  return createSelector(selectSalesDevelopmentState(subMarket), state => state.data);
}
